import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { Loop } from '@/common/api/types/loops';
import { AgenticInsightsApi } from '@/features/agentic-insights/api/agenticInsightsApi';
import type { AgenticRunDataSource } from '@/features/agentic-insights/components/data/agenticRunDataSource';
import { getLatestRunFixture } from '@/features/agentic-insights/components/data/fixtures';
import type { PersonaView } from '@/features/agentic-insights/components/data/resolvePersonaView';
import { resolvePersonaViews } from '@/features/agentic-insights/components/data/resolvePersonaView';
import {
  RUN_POLL_INTERVAL_MS,
  resolveEffectiveRunStatus,
  resolveRunPollInterval,
} from '@/features/agentic-insights/components/data/runLifecycle';
import type {
  AgenticRun,
  Finding,
  FindingGroup,
} from '@/features/agentic-insights/components/data/types';
import {
  buildFindingEntries,
  type FindingEntry,
} from '@/features/agentic-insights/components/experimental/shared/data/findingEntries';
import { UNASSIGNED_OBJECTIVE } from '@/features/agentic-insights/components/experimental/shared/data/unassignedObjective';
import { agenticInsightsQueryKeys } from '@/features/agentic-insights/queryKeys';

/**
 * A loop that is not the one in focus polls more slowly than the 5s `RUN_POLL_INTERVAL_MS` a focused
 * read uses. All loops mode fans this query out across every loop in the org, and `resolveRunPollInterval`
 * only lets a `running` one keep polling at all — but a large org can have several loops running at
 * once, and nobody reading "All loops" is watching any one of them closely enough to need a 5s
 * cadence. The in-scope loop's own read (`useAgenticRun`/`useViewedRun`, called separately) is
 * unaffected and keeps polling at the normal rate.
 */
const AGGREGATE_POLL_INTERVAL_MS = RUN_POLL_INTERVAL_MS * 3;

export interface AggregateLoopRun {
  loop: Loop;
  /** `undefined` while loading, `null` if the loop has never had a run. */
  run: AgenticRun | null | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Every loop's latest run, fanned out with `useQueries` over the exact query key `useAgenticRun`
 * uses (`agenticInsightsQueryKeys.latestRun.detail`) — so switching to a loop already read here in
 * All loops mode hits a warm cache, the nav loop's own read (fetched independently) shares this same
 * cache entry, and the trigger mutation's prefix invalidation keeps working untouched.
 */
export const useAggregateLatestRuns = (
  loops: readonly Loop[],
  dataSource: AgenticRunDataSource,
): AggregateLoopRun[] => {
  const results = useQueries({
    queries: loops.map((loop) => ({
      queryKey: agenticInsightsQueryKeys.latestRun.detail(dataSource, loop.id),
      queryFn: (): Promise<AgenticRun | null> =>
        dataSource === 'mock'
          ? Promise.resolve(getLatestRunFixture(loop.id))
          : AgenticInsightsApi.getLatestRun(loop.id),
      refetchInterval: (query: { state: { data: AgenticRun | null | undefined } }) =>
        resolveRunPollInterval(query.state.data, Date.now()) === false
          ? false
          : AGGREGATE_POLL_INTERVAL_MS,
      placeholderData: (previousData: AgenticRun | null | undefined) => previousData,
    })),
  });

  // `useQueries` returns a fresh array on every render, so without this a fresh `AggregateLoopRun[]`
  // would follow it — recomputing every consumer downstream (the merge, `resolvePersonaViews` per
  // loop, `useFindingRefs`, the objective-id set) on renders that touched neither the loop list nor
  // any query's own data. Memoised on the inputs actually shaped like ones that matter; still
  // recomputes on every real query update, since `results` is a new array whenever any of them are,
  // but no longer on unrelated page state (an objective pill selected, a menu opened, and so on).
  return useMemo(
    () =>
      loops.map((loop, index) => {
        const result = results[index];
        return {
          loop,
          run: result?.data,
          isLoading: result?.isLoading ?? false,
          isError: result?.isError ?? false,
        };
      }),
    [loops, results],
  );
};

/**
 * A loop with a stored, completed run — the set a reader can actually see something in.
 *
 * Goes through `resolveEffectiveRunStatus` rather than testing `run.status === 'completed'`
 * directly: `status` is optional in the contract, and every other reader of a run — including the
 * single-loop reading this same page falls back to — treats an absent `status` as a pre-background
 * run that finished, not as an unreadable one. A literal comparison would silently drop that loop
 * from the merge (with the banner's own count agreeing that nothing is missing) while its findings
 * still show the moment you open it directly. The same call also degrades a `running` run whose
 * heartbeat has gone stale to `failed`, which is what keeps a dead run from being read as live here.
 */
export const readableLoopRuns = (
  runs: readonly AggregateLoopRun[],
): (AggregateLoopRun & { run: AgenticRun })[] =>
  runs.filter(
    (entry): entry is AggregateLoopRun & { run: AgenticRun } =>
      entry.run !== null &&
      entry.run !== undefined &&
      resolveEffectiveRunStatus(entry.run, Date.now()) === 'completed',
  );

/**
 * The raw count a loop's latest run carries, before any persona voices or withholds it — what the
 * loop switcher shows beside each row. Raw rather than persona-filtered: computing every loop's
 * voiced set just to answer "is this worth opening" would mean resolving persona views for loops the
 * reader has not chosen to read, and the count only has to answer that one question.
 *
 * `null` covers every reason a loop has nothing to show (never run, still generating, failed) except
 * `running`, which the caller renders as a spinner instead — see the switcher's own decision on this.
 */
export const rawFindingCount = (entry: AggregateLoopRun): number | null =>
  entry.run && resolveEffectiveRunStatus(entry.run, Date.now()) === 'completed'
    ? entry.run.findings.length
    : null;

export interface AggregateFindingEntry extends FindingEntry {
  /** Which loop this finding came from — required in the merged reading; absent findings carry no
   * loop id of their own. Stamped here in the UI layer rather than expected on the DTO. */
  loop: Loop;
}

/**
 * `finding.id` is a per-run slug (`objective-3-no-work-attached`), not a value the contract makes
 * unique across loops — two loops' runs can and, in sample-data mode, always do produce the exact
 * same id. In a single-loop reading that never matters; in the merged All loops reading it means a
 * bare `finding.id` can no longer be trusted as a lookup or comparison key — the `views` map would
 * collapse two loops' persona views into one (last loop wins), an open finding would expand every
 * loop's copy of it at once, and the Details rail would resolve a click on loop C's row to loop A's
 * finding. Every place that keys on a finding's identity — the `views` map, the open/selected
 * finding in the URL, the React key on a rendered row — uses this instead, wherever the entry might
 * be a merged one, so the loop is always folded into the identity when there is more than one.
 */
export const entryKey = (entry: Pick<FindingEntry, 'finding'> & { loop?: Loop }): string =>
  entry.loop ? `${entry.loop.id}:${entry.finding.id}` : entry.finding.id;

const matchesObjective = (finding: Finding, objective: string | undefined): boolean => {
  if (objective === undefined) return true;
  if (objective === UNASSIGNED_OBJECTIVE) return finding.objectiveIds.length === 0;
  return finding.objectiveIds.includes(objective);
};

/**
 * Filters by objective and renumbers per group, generically over plain and loop-stamped entries
 * alike.
 *
 * A close cousin of `shared/data/findingEntries.ts`'s `filterFindingEntries`, but generic where that
 * one is typed to `FindingEntry[]` — the aggregate reading needs the same filter-then-renumber pass
 * over entries that also carry a `loop`, and the shared version's return type would erase that field
 * even though the object survives the spread at runtime. Kept here rather than widening the shared
 * signature: nothing else that imports it needs the extra field, so widening it there would be
 * generality nobody but this variant asked for.
 */
export const filterEntries = <T extends FindingEntry>(
  entries: T[],
  objective: string | undefined,
): T[] => {
  const seenInGroup = new Map<FindingGroup, number>();

  return entries
    .filter((entry) => matchesObjective(entry.finding, objective))
    .map((entry) => {
      const ordinal = (seenInGroup.get(entry.group) ?? 0) + 1;
      seenInGroup.set(entry.group, ordinal);
      return { ...entry, ordinal };
    });
};

export interface AggregateReading {
  findings: Finding[];
  entries: AggregateFindingEntry[];
  /** Keyed by `entryKey`, not by the raw `finding.id` — see that function's own comment. */
  views: Map<string, PersonaView>;
  readableLoopCount: number;
  totalFindings: number;
  withheldCount: number;
}

/**
 * Every readable loop's findings, merged and stamped with the loop they came from — the All loops
 * reading. Ordinals restart per group across the merge, the same rule a single run's findings follow,
 * so "3rd risk" always means the 3rd risk finding a reader would reach scrolling down, not the 3rd
 * risk of whichever loop happened to author it.
 *
 * `personaId` is one shared choice across every loop's findings, same as the single-loop reading: the
 * role picker's options come from the nav loop's own run (see the page), and this just applies
 * whichever role is selected to each loop's findings against that loop's own core-persona id.
 */
export const buildAggregateReading = (
  runs: readonly AggregateLoopRun[],
  personaId: string,
): AggregateReading => {
  const readable = readableLoopRuns(runs);
  const views = new Map<string, PersonaView>();
  const findings: Finding[] = [];
  let withheldCount = 0;

  const perLoopEntries = readable.map(({ loop, run }) => {
    findings.push(...run.findings);
    const runViews = resolvePersonaViews(run.findings, personaId, run.personaId ?? '');
    // Re-keyed per loop before merging — `runViews` comes back keyed by the raw, per-run
    // `finding.id`, which is exactly the collision `entryKey` exists to avoid; folding the loop in
    // here is what keeps a same-id finding from a second loop from overwriting the first's view.
    for (const [id, view] of runViews) {
      views.set(`${loop.id}:${id}`, view);
      if (view.kind === 'withheld') withheldCount += 1;
    }
    return buildFindingEntries(run.findings, runViews).map((entry) => ({ ...entry, loop }));
  });

  // Concatenated loop by loop rather than interleaved: a reader scanning one group top to bottom
  // reads one loop's findings at a time instead of a shuffled order with no meaning.
  const merged = perLoopEntries.flat();
  const seenInGroup = new Map<FindingGroup, number>();
  const entries = merged.map((entry) => {
    const ordinal = (seenInGroup.get(entry.group) ?? 0) + 1;
    seenInGroup.set(entry.group, ordinal);
    return { ...entry, ordinal };
  });

  return {
    findings,
    entries,
    views,
    readableLoopCount: readable.length,
    totalFindings: entries.length,
    withheldCount,
  };
};
